import resources from 'app/translations/resources';
import { isEdelvives } from './utils';
// import edelvivesResources from 'edelvives/translations/resources';

// Idioma por defecto de la app, alineado con el fallbackLng de i18next.
export const DEFAULT_LANGUAGE = 'es';

export function importLanguageResources(tenant, defaultLanguage) {
  // En allLanguages tenemos todas las claves del archivo resources, que contiene los idiomas origen y los específicos de cada cliente
  // ej ['es', 'en', 'es_MACMILLAN', 'en_GOTITBYME'....]
  const allLanguages = Object.keys(resources);
  // En availableLanguages vamos a obtener todos los idiomas que pueden existir en la app
  const availableLanguages = getAvailableLanguages();
  let languageKeys = {};
  const tenantName = tenant?.name;

  availableLanguages.forEach((lang) => {
    // Si en allLanguages tenemos el idioma específico del tenant, lo añadimos, si no, añadimos el idioma origen
    if (isEdelvives()) {
      const key = lang + '_Edelvives Digital Plus';
      // Fallback: primero busca el idioma específico del tenant, luego el idioma base, y finalmente array vacío
      languageKeys[lang] = resources[key] || resources[lang] || [];
    } else if (allLanguages.includes(lang + '_' + tenantName)) {
      languageKeys[lang] = resources[lang + '_' + tenantName];
    } else if (defaultLanguage !== 'es' && allLanguages.includes(defaultLanguage)) {
      languageKeys[lang] = resources[defaultLanguage];
    } else {
      // Fallback a array vacío si no existe el idioma
      languageKeys[lang] = resources[lang] || [];
    }
  });

  return languageKeys;
}

// Idiomas que tienen bundle de traducción real (claves de resources sin el sufijo de tenant).
// Es la whitelist de idiomas con interfaz: cualquier otro id (p. ej. los hexadecimales que
// genera POST /langs para versiones de contenido) no puede pintarse en la UI porque caería
// al fallbackLng de i18next.
export function getAvailableLanguages() {
  return [...new Set(Object.keys(resources).map((lang) => lang.split('_')[0]))];
}

/**
 * Idiomas de interfaz disponibles del tenant (respuesta de `GET /langs`) que además tienen
 * bundle de traducción. Es la lista contra la que se coerciona el idioma del usuario.
 *
 * Devuelve `[]` mientras no hay respuesta, para que la coerción no decida con la lista vacía.
 *
 * @param {Array<string|{id: string}>} tenantLanguages idiomas del tenant desde el duck de langs
 * @returns {string[]} ids de idioma con bundle
 */
export function getInterfaceLanguageIds(tenantLanguages) {
  if (!Array.isArray(tenantLanguages)) return [];
  const languagesWithBundle = getAvailableLanguages();
  return tenantLanguages
    .map((language) => (typeof language === 'string' ? language : language?.id))
    .filter((id) => !!id && languagesWithBundle.includes(id));
}

/**
 * Garantiza que el idioma activo tiene bundle cargado: si no lo tiene se prueba su idioma base
 * (`pt-BR` → `pt`, como ya resolvía i18next), después el idioma del tenant y, en último caso, el
 * idioma por defecto. Evita que i18next resuelva por `fallbackLng` con un `lang_id` sin recursos
 * (p. ej. los ids hexadecimales de las versiones de contenido).
 *
 * @param {string} language idioma resuelto
 * @param {string} [tenantLanguage] `tenant_lang_id`
 * @returns {string} idioma con bundle garantizado
 */
export function getLanguageWithBundle(language, tenantLanguage) {
  const languagesWithBundle = getAvailableLanguages();
  if (language && languagesWithBundle.includes(language)) return language;

  const baseLanguage = typeof language === 'string' ? language.split(/[-_]/)[0] : null;
  if (baseLanguage && languagesWithBundle.includes(baseLanguage)) return baseLanguage;

  if (tenantLanguage && languagesWithBundle.includes(tenantLanguage)) return tenantLanguage;
  return DEFAULT_LANGUAGE;
}
